import { CommonModule } from '@angular/common';
import { Component, computed, forwardRef, input, linkedSignal, model, output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cn } from '@cf/core';
import { cva, VariantProps } from 'class-variance-authority';

const checkboxVariants = cva('', {
  variants: {
    checked: {
      true: ['tw:text-blue-50'],
      false: [],
    },
    indeterminate: {
      true: ['tw:text-blue-50'],
      false: [''],
    },
    disabled: {
      true: ['tw:text-gray-40'],
      false: [],
    },
  },
  compoundVariants: [
    { checked: false, indeterminate: false, disabled: false, class: 'tw:text-gray-70' },
  ],
  defaultVariants: {
    disabled: false,
  },
});

const checkboxLabelVariants = cva(
  'tw:inline-flex tw:items-center tw:gap-2 tw:cursor-pointer tw:peer-disabled:cursor-not-allowed',
  {
    variants: {
      disabled: {
        true: ['tw:text-gray-40'],
        false: ['tw:text-gray-80'],
      },
      size: {
        xs: ['tw:text-xs tw:gap-1'],
        sm: ['tw:text-sm tw:gap-1'],
        md: ['tw:text-base tw:gap-2'],
        lg: ['tw:text-lg tw:gap-2'],
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

export type Variants = VariantProps<typeof checkboxLabelVariants> &
  VariantProps<typeof checkboxVariants>;
export type Sizes = Variants['size'];

export interface CheckboxChangeEvent {
  source: CheckboxComponent;
  checked: boolean;
}

@Component({
  selector: 'cf-checkbox',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './checkbox.component.html',
  styleUrl: './checkbox.component.css',
  host: {
    class: 'tw:inline-flex tw:leading-none',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent<T = any> implements ControlValueAccessor {
  id = input(`checkbox-${Math.random().toString(36).substring(2, 9)}`);

  checked = model(false);
  indeterminate = model(false);
  disabled = input(false);
  value = input<T | null>(null);
  // eslint-disable-next-line @angular-eslint/no-output-native
  change = output<CheckboxChangeEvent>();
  size = input<Sizes>('sm');
  labelClasses = input<string>('');

  disabledState = linkedSignal(() => this.disabled());
  checkedValue = computed(() => {
    if (this.indeterminate()) {
      return null;
    }

    return this.checked();
  });

  checkboxClasses = computed(() =>
    cn(
      checkboxVariants({
        checked: this.checked(),
        indeterminate: this.indeterminate(),
        disabled: this.disabled(),
      }),
    ),
  );

  checkboxLabelClasses = computed(() =>
    cn(
      checkboxLabelVariants({
        disabled: this.disabled(),
        size: this.size(),
      }),
      this.labelClasses(),
    ),
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (_value: boolean | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  handleCheckboxChange(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled()) {
      return;
    }

    this.indeterminate.set(false);
    this.checked.set((event.target as HTMLInputElement).checked);
    this.onChange(this.checkedValue());
    this.change.emit({
      source: this,
      checked: this.checked(),
    });
    this.onTouched();
  }

  handleCheckboxBlur() {
    this.onTouched();
  }

  writeValue(value: boolean | null) {
    this.indeterminate.set(value === null);
    this.checked.set(value ?? false);
  }

  registerOnChange(fn: (value: boolean | null) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabledState.set(isDisabled);
  }
}
